import { Divider } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';

import { useProductCheckoutLink } from '../../../../features/checkout/hooks/useProductCheckoutLink';
import { useDrawButtonLabels } from '../../../../features/draws/hooks/useDrawButtonLabels';
import { useDrawStage } from '../../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useDraws } from '../../../../providers/DrawProvider';
import { isInNextSixDays } from '../../../../utils/product';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { DrawEntryButton } from './DrawEntryButton';
import { DrawExplainer } from './DrawExplainer';
import { PreOrderHeader } from './PreOrderHeader';

export const DrawFlow = () => {
  const { t } = useTranslation(['product', 'authentication']);
  const [showDialog, setShowDialog] = useState(false);

  const { isAccountInDraw, linkedDraws, mainDraw } = useDraws();

  const stage = useDrawStage(mainDraw?.draw);
  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const checkoutLink = useProductCheckoutLink();
  const labels = useDrawButtonLabels('mainCta', isPreOrder);

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'draw',
    });
  }, [emitter]);

  if (!mainDraw) {
    return null;
  }

  const drawButton = (
    <DrawEntryButton
      checkoutLink={checkoutLink}
      entered={isAccountInDraw(mainDraw.draw.id)}
      label={
        linkedDraws.length > 0
          ? t('drawers.review.draw.multiProduct.drawButtonTitle')
          : t(labels.normal)
      }
      mainDraw={mainDraw}
      successLabel={t('subscriptionCta.draw.buttonSuccessTitle')}
    />
  );

  return (
    <>
      {stage === 'DRAW_PRE_ORDER_CLOSED' && (
        <>
          <PreOrderHeader active={false} title={t('preOrder.isClosed.title')} />
          <Divider className="my-6" variant="dashed" />
        </>
      )}
      <div className="mb-6 flex w-full flex-col space-y-3 text-center">
        <div>
          <p className="w-full font-bold">
            {isInNextSixDays(new Date(mainDraw.draw.closeAt))
              ? t('subscriptionDate.draw.countdown')
              : t('subscriptionDate.draw.date')}
          </p>
          <p className="w-full">
            <ProductDate date={new Date(mainDraw.draw.closeAt)} />
          </p>
        </div>
        {mainDraw.draw.allocation && (
          <p className="w-full text-neutral-4">
            {t(
              stage === 'DRAW_PRE_ORDER_CLOSED'
                ? 'subscriptionCta.preOrder.titleAllocation'
                : 'subscriptionCta.draw.titleAllocation',
              { amount: mainDraw.draw.allocation },
            )}
          </p>
        )}
      </div>
      {drawButton}
      <button
        className="hyperlink mt-3"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.draw.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.draw.learnMoreDialog.title')}
      >
        <DrawExplainer />
        {showDialog && drawButton}
      </SimpleDialog>
    </>
  );
};
